import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { dismissBootSplash } from "@/core/lib/boot-splash";
import { getQueryClient } from "@/core/lib/query-client";
import { initSentry } from "@/core/lib/sentry";
import { installSessionExpiryHandler } from "@/core/lib/session-expiry";
import {
  isStaleChunkError,
  RouteError,
  RouteNotFound,
  reloadForStaleChunk,
} from "@/ui/components/common/route-error";
import { RoutePending } from "@/ui/components/skeletons/route-pending";

import "./index.css";
import { routeTree } from "./routeTree.gen";

initSentry();

/**
 * One client, shared by the router's loaders and the components that render
 * from them — a loader's `ensureQueryData` and a component's `useQuery` must
 * hit the same cache entry for the loader to actually prevent a refetch.
 */
const queryClient = getQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // The loaders read through React Query, which owns staleness; leaving the
  // router's own cache to expire independently would double-fetch.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // Guards and loaders are async, so there is always a gap between a route
  // being asked for and being renderable. With no pending component the router
  // renders nothing for that gap — on a cold load that is a bare, empty document,
  // which is the "blank screen" the app showed on the way into a signed-in
  // surface. `defaultPendingMs` keeps it off the screen entirely for hops that
  // resolve quickly, so this only appears when there really is a wait.
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  // Without these a failed loader or render fell through to the router's bare
  // built-in error page — the "system error" people hit while browsing.
  defaultErrorComponent: RouteError,
  defaultNotFoundComponent: RouteNotFound,
});

// The boot splash in index.html covers the whole first load: the bundle, the
// guards and the first route's loaders. It lifts once that route has resolved
// (or failed into its error screen), never on a pending screen in between.
const stopSplashWatch = router.subscribe("onResolved", () => {
  stopSplashWatch();
  dismissBootSplash();
});

// A deploy renames the hashed chunks, so a tab opened before it fails its next
// lazy load — often a hover preload, which never reaches the error screen.
// Reload once onto the new build instead.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadForStaleChunk()) event.preventDefault();
});
window.addEventListener("unhandledrejection", (event) => {
  if (isStaleChunkError(event.reason) && reloadForStaleChunk()) event.preventDefault();
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

installSessionExpiryHandler(queryClient, {
  currentHref: () => router.state.location.href,
  goToSignIn: (redirect) =>
    router.navigate({
      to: "/sign-in",
      search: { redirect, email: undefined, denied: undefined },
      replace: true,
    }),
  onExpired: () =>
    toast.error("Your session has expired", {
      description: "Sign in again to pick up where you left off.",
    }),
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root is missing from index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  </StrictMode>,
);
